/**
 * The most recent thing deck.gl picked.
 *
 * Nothing in here asks deck to pick. Calling `pickObject()` by hand while deck
 * is interleaved with MapLibre means both libraries bind framebuffers against
 * the same WebGL context, and deck's picking pass can end up drawing to the
 * screen instead of offscreen — the canvas goes black apart from a small
 * square of picking colours under the cursor, which is exactly the failure
 * this replaces. Driving that from a `mousemove` handler made it happen on
 * every frame of every cursor movement.
 *
 * deck already picks on hover and on click, inside its own render loop where
 * that state is managed. The overlay records what it found; readers take it
 * from here rather than asking for a pick of their own.
 */

let hover = null;
let click = null;

const hoverListeners = new Set();
const clickListeners = new Set();

/** Misses are recorded too — "nothing under the cursor" is an answer. */
export function recordHover(info) {
  hover = info?.object ? info : null;
  for (const listener of hoverListeners) listener(hover);
}

export function recordClick(info) {
  click = info?.object ? info : null;
  for (const listener of clickListeners) listener(click);
}

export const hovered = () => hover;
export const clicked = () => click;

/**
 * Subscriptions, for the readers that a poll cannot serve.
 *
 * deck dispatches its click through a promise: the pick from pointer-down is
 * awaited, so `onClick` — and recordClick with it — runs in a microtask AFTER
 * MapLibre has already called every one of its own `click`/`touchend`
 * listeners synchronously. A MapLibre listener that reads `clicked()` is
 * therefore always exactly one click behind: it sees the previous tap's
 * answer, which on a street corner means the previous tap's incident.
 *
 * Hover has the same shape one frame smaller — deck batches hover picks to
 * the next animation frame, so the render done on the last `mousemove` can be
 * one object behind, and a cursor that then stops never corrects itself.
 *
 * So consumers that act on a pick subscribe here and are called when the pick
 * actually lands. Misses notify too, with null: "the tap hit nothing" is an
 * answer a tooltip needs.
 */
export function onHoverPick(listener) {
  hoverListeners.add(listener);
  return () => hoverListeners.delete(listener);
}

export function onClickPick(listener) {
  clickListeners.add(listener);
  return () => clickListeners.delete(listener);
}
